import styles from "@/styles/CatalogueCell.module.css";
import { Castle } from "./Database";
import { Inter } from "next/font/google";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const inter = Inter({ subsets: ["latin"] });;

type CatalogueCellProps = {
  content: Castle;
}

export default function CatalogueCell({ content }: CatalogueCellProps) {
  const router = useRouter();
  const nl = router.asPath.includes("/nl/");
  const fr = router.asPath.includes("/fr/");

  const amountRef = useRef<HTMLInputElement>(null);

  const [multipleCastles, setMultipleCastles] = useState<string[] | null>([]);
  const [multipleCastlesUnformatted, setMultipleCastlesUnformatted] = useState<string | null>(null);

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const images = [content.imagePath];

  const [promotionVariable, setPromotionVariable] = useState<boolean | null>(null);

  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const newRequestsUnformatted = sessionStorage.getItem("multipleCastles");
      const newAmount = sessionStorage.getItem(content.nameNL);
      const newPromotionVariable = sessionStorage.getItem("specialPromo");
      if (newRequestsUnformatted !== multipleCastlesUnformatted) {
        setMultipleCastlesUnformatted(newRequestsUnformatted);
        setMultipleCastles(newRequestsUnformatted ? newRequestsUnformatted.split(",").concat() : null);
      }
      if (newPromotionVariable !== promotionVariable) {
        setPromotionVariable(newPromotionVariable === "TRUE");
      }
    }, 100);
    return () => clearInterval(intervalId);
  }, [multipleCastles, multipleCastlesUnformatted, content.nameNL, promotionVariable]);

  useEffect(() => {
    if (isViewerOpen) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = ""; // Enable scrolling
    }
    return () => {
      document.body.style.overflow = ""; // Re-enable scrolling when component unmounts
    };
  }, [isViewerOpen]);

  // Check clicked outside, close
  useEffect(() => {
    const handleOutSideClick = (event: Event) => {
      if (event.target != null && !imageRef.current?.contains(event.target as Node)) {
        setIsViewerOpen(false);
      }
    };
    window.addEventListener("mousedown", handleOutSideClick);
    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
    };
  }, [imageRef]);

  const openImageViewer = useCallback((index: number) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  function requestButtonText() {
    if (nl) {
      return <>Aanvragen</>;
    } else if (fr) {
      return <>Demander</>;
    }
  }

  function requestClicked() {
    if (multipleCastles && multipleCastles.includes(content.nameNL)) {
      const resultingCastles = multipleCastles.filter((castle) => castle !== content.nameNL).join(",");
      if (resultingCastles === "") {
        sessionStorage.removeItem("multipleCastles");
      } else {
        sessionStorage.setItem("multipleCastles", resultingCastles);
      }
    } else {
      if (sessionStorage.getItem("firstChoiceNL") !== null || sessionStorage.getItem("secondChoiceNL") !== null) {
        sessionStorage.removeItem("firstChoiceNL");
        sessionStorage.removeItem("firstChoiceFR");
        sessionStorage.removeItem("secondChoiceNL");
        sessionStorage.removeItem("secondChoiceFR");
      }
      sessionStorage.setItem(
        "multipleCastles",
        multipleCastles ? multipleCastles.concat(content.nameNL).join(",") : content.nameNL
      );
    }
  }

  function showRequestButton() {
    if (multipleCastles && multipleCastles.includes(content.nameNL)) {
      return (
        <button
          className={styles.requestButton}
          onClick={() => requestClicked()}
          style={{ backgroundColor: "#4D7EA8", color: "white" }}
        >
          {requestButtonText()}
        </button>
      );
    } else {
      return (
        <button className={styles.requestButton} style={{ backgroundColor: "#f0f0f0", color: "black" }} onClick={() => requestClicked()}>
          {requestButtonText()}
        </button>
      );
    }
  }

  return (
    <div id={content.nameNL} className={styles.catalogueCell}>
      {nl && (
        <>
          {content.imagePath ? (
            <>
              <img
                src={content.imagePath}
                alt="Foto van springkasteel"
                width={300}
                height={300}
                onClick={() => openImageViewer(0)}
                style={{ cursor: "pointer" }}
              />
              {isViewerOpen && (
                <div className={styles.imageViewer}>
                  <div className={styles.wrapper}>
                    <button className={styles.closeButton} onClick={closeImageViewer}>
                      <X size={40} />
                    </button>
                    <div className={styles.content}>
                      <img
                        src={images[currentImage]}
                        alt="Afbeelding van een springkasteel"
                        width={300}
                        height={300}
                        style={{ maxWidth: "none", maxHeight: "none" }}
                        ref={imageRef}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ width: "300px", height: "300px" }}></div>
          )}
          <p className={styles.name}>{content.nameNL}</p>
          <p className={styles.extraInfo}>{content?.extraNL}</p>
          <p className={styles.dimensions}>
            B: {content.width}m &ndash; D: {content.depth}m &ndash; H: {content.height}m
          </p>
          <p className={styles.price}>&euro;{content.price} / dag*</p>
          {promotionVariable !== null && promotionVariable && (
            <div className={styles.promoBox}>
              <div className={styles.oldPrice}>
                <p className={styles.scratchedPrice}>&euro;{(content as Castle).promotion}</p>
                <p className={styles.promoDays}>/ 2 dagen*</p>
              </div>
              <div className={styles.specialPromo}>
                <p className={styles.promo}>&euro;{(content as Castle).price}</p>
                <p className={styles.promoText}>PROMO</p>
              </div>
            </div>
          )}
          {promotionVariable !== null && !promotionVariable && (
            <p className={styles.promo}>&euro;{(content as Castle).promotion} / 2 dagen*</p>
          )}
        </>
      )}
      {fr && (
        <>
          {content.imagePath ? (
            <>
              <img
                src={content.imagePath}
                alt="Photo d'un château gonflable"
                width={300}
                height={300}
                onClick={() => openImageViewer(0)}
                style={{ cursor: "pointer" }}
              />
              {isViewerOpen && (
                <div className={styles.imageViewer}>
                  <div className={styles.wrapper}>
                    <button className={styles.closeButton} onClick={closeImageViewer}>
                      <X size={40} />
                    </button>
                    <div className={styles.content}>
                      <img
                        src={images[currentImage]}
                        alt="Photo d'un château gonflable"
                        width={300}
                        height={300}
                        style={{ maxWidth: "none", maxHeight: "none" }}
                        ref={imageRef}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ width: "300px", height: "300px" }}></div>
          )}
          <p className={styles.name}>{content.nameFR}</p>
          <p className={styles.extraInfo}>{content?.extraFR}</p>
          <p className={styles.dimensions}>
            L: {content.width}m &ndash; P: {content.depth}m &ndash; H: {content.height}m
          </p>
          <p className={styles.price}>&euro;{content.price} / jour</p>
          {promotionVariable !== null && promotionVariable && (
            <div className={styles.promoBox}>
              <div className={styles.oldPrice}>
                <p className={styles.scratchedPrice}>&euro;{(content as Castle).promotion}</p>
                <p className={styles.promoDays}>/ 2 jours</p>
              </div>
              <div className={styles.specialPromo}>
                <p className={styles.promo}>&euro;{(content as Castle).price}</p>
                <p className={styles.promoText}>PROMO</p>
              </div>
            </div>
          )}
          {promotionVariable !== null && !promotionVariable && (
            <p className={styles.promo}>&euro;{(content as Castle).promotion} / 2 jours</p>
          )}
        </>
      )}
      <div className={styles.buttons}>{showRequestButton()}</div>
    </div>
  );
}
